"use client";
import Image from "next/image";
import { useTranslations } from "next-intl";

type Person = { src: string; name: string; role: string };

type CaseStudyConfig = {
  key: "cs1" | "cs2";
  people: Person[];
  hasSecondary: boolean;
};

const STUDIES: CaseStudyConfig[] = [
  {
    key: "cs1",
    people: [
      {
        src: "/people/hannah-kuempel.jpg",
        name: "Hannah Kümpel",
        role: "Operations · Stromstrategen",
      },
      {
        src: "/people/philipp-wenzl.jpg",
        name: "Philipp Wenzl",
        role: "Gesellschafter · Stromstrategen",
      },
    ],
    hasSecondary: true,
  },
  {
    key: "cs2",
    people: [
      {
        src: "/people/sebastian-klingel.jpg",
        name: "Sebastian Klingel",
        role: "Gründer · Neumandat",
      },
    ],
    hasSecondary: false,
  },
];

export const CaseStudies = () => {
  const t = useTranslations("caseStudies");

  return (
    <section className="dark relative w-full overflow-hidden bg-neutral-950">
      <div className="absolute inset-x-0 top-0 h-px bg-neutral-800" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-neutral-800" />

      <div className="relative mx-auto max-w-6xl px-4 md:px-6 lg:px-8 py-20 md:py-28">
        <div className="max-w-2xl">
          <h2 className="text-3xl md:text-5xl font-bold leading-tight tracking-tight text-white">
            {t("heading")}
          </h2>
        </div>

        <div className="mt-16 space-y-20">
          {STUDIES.map((study) => (
            <article
              key={study.key}
              className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-8 md:gap-12 items-start border-t border-neutral-800 pt-12"
            >
              {/* Portraits */}
              <div className="flex flex-row md:flex-col gap-4 md:gap-6 shrink-0">
                {study.people.map((person) => (
                  <figure key={person.name} className="flex flex-col gap-3">
                    <Image
                      src={person.src}
                      alt={person.name}
                      width={300}
                      height={300}
                      className="size-24 md:size-32 rounded-full object-cover ring-2 ring-neutral-800"
                    />
                    <figcaption className="text-xs text-neutral-500 leading-tight max-w-[8rem]">
                      <span className="block text-neutral-200 font-medium">
                        {person.name}
                      </span>
                      <span className="block mt-0.5">{person.role}</span>
                    </figcaption>
                  </figure>
                ))}
              </div>

              {/* Story */}
              <div className="max-w-prose">
                <p className="text-sm font-semibold text-red-500">
                  {t(`${study.key}_tag`)}
                </p>
                <blockquote className="mt-5 text-2xl md:text-3xl font-semibold leading-tight text-white">
                  &bdquo;{t(`${study.key}_quote`)}&ldquo;
                </blockquote>
                <p className="mt-3 text-[11px] uppercase tracking-wider text-neutral-500">
                  {t(`${study.key}_quote_attrib`)}
                </p>
                <p className="mt-6 text-sm md:text-base text-neutral-400 leading-relaxed">
                  {t(`${study.key}_body`)}
                </p>
                {study.hasSecondary && (
                  <div className="mt-6 border-l-2 border-neutral-700 pl-4">
                    <p className="text-base italic text-neutral-300 leading-relaxed">
                      &bdquo;{t(`${study.key}_secondary_quote`)}&ldquo;
                    </p>
                    <p className="mt-2 text-[11px] uppercase tracking-wider text-neutral-500">
                      {t(`${study.key}_secondary_attrib`)}
                    </p>
                  </div>
                )}
                <p className="mt-5 text-[10px] text-neutral-500 uppercase tracking-wider">
                  {t(`${study.key}_industry`)}
                </p>
              </div>

              {/* Metriken */}
              <div className="flex md:flex-col gap-8 md:gap-6 md:border-l md:border-neutral-800 md:pl-10 md:min-w-[180px]">
                {[1, 2, 3].map((i) => (
                  <div key={i}>
                    <div className="text-3xl md:text-4xl font-bold leading-none text-white tabular-nums">
                      {t(`${study.key}_metric_${i}_value`)}
                    </div>
                    <div className="mt-2 text-[10px] text-neutral-500 uppercase tracking-wider">
                      {t(`${study.key}_metric_${i}_label`)}
                    </div>
                  </div>
                ))}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

